import React from "react"
import { ChevronLeft, ChevronRight } from "lucide-mui"
import { DateTime } from "luxon"
import i18n from "@/i18n"
import { type OpeningHour } from "@/utils/opening_hours"
import { isSidebarDateClosed } from "../reservation_sidebar_availability"

type ReservationSidebarCalendarProps = {
  value: DateTime | null
  timezone: string
  country: string
  openingHours?: OpeningHour[]
  onChange: (value: DateTime) => void
}

const formatWeekdayLabel = (date: DateTime) => {
  const shortLabel = date.toFormat("ccc").replace(".", "")
  return shortLabel.slice(0, 2).replace(/^./, (char) => char.toUpperCase())
}

const ReservationSidebarCalendar: React.FC<ReservationSidebarCalendarProps> = ({
  value,
  timezone,
  country,
  openingHours,
  onChange,
}) => {
  const language = i18n.locale || "en"
  const locale = country ? `${language}-${country}` : language
  const today = DateTime.now().setZone(timezone).setLocale(locale).startOf("day")
  const selectedDate = value?.setZone(timezone).setLocale(locale).startOf("day") || null
  const selectedDateIso = selectedDate?.toISODate()
  const [visibleMonth, setVisibleMonth] = React.useState(() => (selectedDate || today).startOf("month"))

  React.useEffect(() => {
    if (!selectedDate) return
    setVisibleMonth((currentMonth) =>
      selectedDate.hasSame(currentMonth, "month") ? currentMonth : selectedDate.startOf("month"),
    )
  }, [selectedDateIso])

  const monthStart = visibleMonth.startOf("month")
  const localeInfo = new Intl.Locale(locale) as Intl.Locale & { weekInfo?: { firstDay?: number } }
  const firstDayOfWeek = localeInfo.weekInfo?.firstDay || 7
  const firstDayIndex = (monthStart.weekday - firstDayOfWeek + 7) % 7
  const daysInMonth = visibleMonth.daysInMonth
  const totalCellCount = Math.ceil((firstDayIndex + daysInMonth) / 7) * 7
  const weekdayStart = DateTime.fromISO("2026-03-02").setLocale(locale)

  const weekdayLabels = Array.from({ length: 7 }, (_, index) => {
    const offset = (firstDayOfWeek - 1 + index) % 7
    return formatWeekdayLabel(weekdayStart.plus({ days: offset }))
  })
  const monthDays = Array.from({ length: totalCellCount }, (_, index) => {
    const dayNumber = index - firstDayIndex + 1

    if (dayNumber < 1 || dayNumber > daysInMonth) return null

    return monthStart.set({ day: dayNumber }).startOf("day")
  })

  return (
    <div className="space-y-4" data-testid="public-venue-sidebar-calendar">
      <div className="flex items-center justify-between text-[#374151]">
        <button
          type="button"
          onClick={() => setVisibleMonth((currentMonth) => currentMonth.minus({ months: 1 }).startOf("month"))}
          className="flex h-[22px] w-[22px] items-center justify-center rounded-md text-[#171717] transition-colors hover:bg-[#f4f6f8]"
          aria-label={i18n.t("app.public.venue.sidebar.previous_month")}
          data-testid="public-venue-sidebar-month-prev"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        <div className="text-sm font-medium leading-5 text-[#374151]" data-testid="public-venue-sidebar-month-label">
          {visibleMonth.toFormat("LLLL yyyy")}
        </div>

        <button
          type="button"
          onClick={() => setVisibleMonth((currentMonth) => currentMonth.plus({ months: 1 }).startOf("month"))}
          className="flex h-[22px] w-[22px] items-center justify-center rounded-md text-[#171717] transition-colors hover:bg-[#f4f6f8]"
          aria-label={i18n.t("app.public.venue.sidebar.next_month")}
          data-testid="public-venue-sidebar-month-next"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-x-[4px] gap-y-[6px]">
        {weekdayLabels.map((label) => (
          <div
            key={label}
            className="flex h-[22px] items-center justify-center text-[13px] leading-5 text-[#171717]"
            data-testid={`public-venue-sidebar-weekday-${label.toLowerCase()}`}
          >
            {label}
          </div>
        ))}

        {monthDays.map((day, index) => {
          if (!day) {
            return <div key={`empty-${index}`} className="h-[34px]" aria-hidden="true" />
          }

          const isoDate = day.toISODate() || `${visibleMonth.year}-${visibleMonth.month}-${day.day}`
          const isClosed = isSidebarDateClosed(day, openingHours, timezone)
          const isDisabled = day < today || isClosed
          const isSelected = Boolean(selectedDate?.hasSame(day, "day"))
          const ariaLabel = `${day.toFormat("EEEE, LLLL d, yyyy")}${isDisabled ? `, ${i18n.t("app.public.venue.sidebar.unavailable")}` : ""}`

          return (
            <button
              key={isoDate}
              type="button"
              disabled={isDisabled}
              onClick={() => onChange(day.startOf("day"))}
              aria-label={ariaLabel}
              className={`flex h-[34px] items-center justify-center rounded-[3px] border bg-white text-[12px] leading-5 transition-colors ${
                isDisabled
                  ? "cursor-not-allowed border-[#e9e9e9] text-[#374151] opacity-30"
                  : isSelected
                    ? "border-[#cfd4dc] font-semibold text-[#374151]"
                    : "border-[#e9e9e9] text-[#4b5563] hover:border-[#d7dce2]"
              }`}
              aria-pressed={isSelected}
              data-selected={isSelected}
              data-testid={`public-venue-sidebar-day-${isoDate}`}
            >
              {day.day}
            </button>
          )
        })}
      </div>
    </div>
  )
}

export default ReservationSidebarCalendar
